import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import DailyFeelingWidget from "@/components/DailyFeelingWidget";
import WeeklyCalendar from "@/components/WeeklyCalendar";
import InstallPWABanner from "@/components/InstallPWABanner";
import PushNotificationToggle from "@/components/PushNotificationToggle";
import { BookOpen, MessageCircle, BookMarked, TrendingUp, Sparkles } from "lucide-react";

const Dashboard = () => {
  const { user, language } = useContext(AuthContext);
  const navigate = useNavigate();
  const [reflection, setReflection] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const [reflectionRes, progressRes] = await Promise.all([
        axios.get(`${API}/daily-reflection`, { headers }),
        axios.get(`${API}/progress`, { headers })
      ]);

      setReflection(reflectionRes.data);
      setProgress(progressRes.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const t = (es, ca) => language === "ca" ? ca : es;
  
  const getWelcomeText = () => {
    const gender = user?.gender;
    if (language === "ca") {
      if (gender === "female") return `Benvinguda, ${user.name}!`;
      if (gender === "male") return `Benvingut, ${user.name}!`;
      return `Benvingut/da, ${user.name}!`;
    } else {
      if (gender === "female") return `¡Bienvenida, ${user.name}!`;
      if (gender === "male") return `¡Bienvenido, ${user.name}!`;
      return `¡Bienvenid@, ${user.name}!`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950">
        <Navigation />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="animate-pulse text-teal-300 text-xl">Cargando...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950">
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Welcome Section */}
        <div data-testid="dashboard-welcome" className="mb-8 fade-in">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 glow-text">
            {getWelcomeText()}
          </h1>
          <p className="text-lg text-gray-300">
            {t("Continúa tu viaje de automaestría consciente", "Continua el teu viatge d'autoconeixement conscient")}
          </p>
        </div>

        {/* Install PWA Banner - Only mobile, first 3 visits */}
        <InstallPWABanner />

        {/* Daily Affirmation - PRIMER */}
        {reflection && (
          <div data-testid="daily-reflection" className="mb-6 fade-in" style={{animationDelay: '0.05s'}}>
            <div className="glass rounded-2xl p-4 border border-teal-400/15">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-teal-300" />
                <h2 className="text-lg font-semibold text-teal-200">
                  {t("Afirmación del Día", "Afirmació del Dia")}
                </h2>
              </div>
              <p className="text-base text-gray-200 leading-relaxed italic">
                "{language === "ca" ? reflection.message_ca : reflection.message_es}"
              </p>
            </div>
          </div>
        )}

        {/* Daily Feeling Widget - SEGON */}
        <div className="mb-6 fade-in" style={{animationDelay: '0.1s'}}>
          <DailyFeelingWidget />
        </div>

        {/* Weekly Activity Calendar - TERCER */}
        <div className="mb-6 fade-in" style={{animationDelay: '0.15s'}}>
          <WeeklyCalendar />
        </div>

        {/* Push Notification Toggle */}
        <div className="mb-12 fade-in flex justify-center" style={{animationDelay: '0.18s'}}>
          <PushNotificationToggle />
        </div>

        {/* Quick Stats - Clicables */}
        {progress && (
          <div data-testid="progress-stats" className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 fade-in" style={{animationDelay: '0.2s'}}>
            <button
              onClick={() => navigate("/lessons")}
              className="glass rounded-2xl p-6 border border-teal-400/10 hover:border-teal-400/30 transition-all text-left"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400">{t("Lecciones Completadas", "Lliçons Completades")}</span>
                <BookOpen className="w-5 h-5 text-teal-300" />
              </div>
              <p className="text-3xl font-bold text-teal-200">{progress.lessons_completed?.length || 0}</p>
              <p className="text-xs text-teal-400/60 mt-2">{t("Clic para ver →", "Clic per veure →")}</p>
            </button>

            <button
              onClick={() => navigate("/exercises")}
              className="glass rounded-2xl p-6 border border-purple-400/10 hover:border-purple-400/30 transition-all text-left"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400">{t("Ejercicios Realizados", "Exercicis Realitzats")}</span>
                <BookMarked className="w-5 h-5 text-purple-300" />
              </div>
              <p className="text-3xl font-bold text-purple-200">{progress.exercises_completed?.length || 0}</p>
              <p className="text-xs text-purple-400/60 mt-2">{t("Clic para ver →", "Clic per veure →")}</p>
            </button>

            <button
              onClick={() => navigate("/journal")}
              className="glass rounded-2xl p-6 border border-amber-400/10 hover:border-amber-400/30 transition-all text-left"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400">{t("Entradas en Diario", "Entrades al Diari")}</span>
                <BookMarked className="w-5 h-5 text-amber-300" />
              </div>
              <p className="text-3xl font-bold text-amber-200">{progress.total_journal_entries || 0}</p>
              <p className="text-xs text-amber-400/60 mt-2">{t("Clic para ver →", "Clic per veure →")}</p>
            </button>
          </div>
        )}

        {/* Quick Actions */}
        <div data-testid="quick-actions" className="fade-in" style={{animationDelay: '0.3s'}}>
          <h2 className="text-2xl font-semibold mb-6 text-gray-200">
            {t("Acciones Rápidas", "Accions Ràpides")}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <button
              data-testid="nav-lessons-btn"
              onClick={() => navigate("/lessons")}
              className="card glass rounded-2xl p-6 text-left hover:border-teal-400/30 transition-all"
            >
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center mb-4">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-teal-200">
                {t("Lecciones", "Lliçons")}
              </h3>
              <p className="text-sm text-gray-400">
                {t("Explora contenido profundo", "Explora contingut profund")}
              </p>
            </button>

            <button
              data-testid="nav-chat-btn"
              onClick={() => navigate("/chat")}
              className="card glass rounded-2xl p-6 text-left hover:border-slate-400/30 transition-all"
            >
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-slate-400 to-slate-600 flex items-center justify-center mb-4">
                <MessageCircle className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-slate-300">
                {t("Chat IA", "Xat IA")}
              </h3>
              <p className="text-sm text-gray-400">
                {t("Conversa con tu guía", "Conversa amb la teva guia")}
              </p>
            </button>

            <button
              data-testid="nav-journal-btn"
              onClick={() => navigate("/journal")}
              className="card glass rounded-2xl p-6 text-left hover:border-amber-400/30 transition-all"
            >
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center mb-4">
                <BookMarked className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold mb-2 gold-accent">
                {t("Diario", "Diari")}
              </h3>
              <p className="text-sm text-gray-400">
                {t("Reflexiona y escribe", "Reflexiona i escriu")}
              </p>
            </button>

            <button
              data-testid="nav-progress-btn"
              onClick={() => navigate("/progress")}
              className="card glass rounded-2xl p-6 text-left hover:border-green-400/30 transition-all"
            >
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-green-300">
                {t("Progreso", "Progrés")}
              </h3>
              <p className="text-sm text-gray-400">
                {t("Visualiza tu avance", "Visualitza el teu avenç")}
              </p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;